import { items } from './PatientChart.utils.tsx'
import type { ChartItem } from './PatientChart.utils.tsx'
import type { Patient } from '@medplum/fhirtypes'
import { Badge, UnstyledButton } from '@mantine/core'
import { cn } from '@/utils/cn.ts'
import type { CSSProperties } from 'react'

type Props = {
  patient: Patient
  onItemClick: (item: ChartItem) => void
  currentPage: string
  className?: string
  /**
   * Optional inline style override. Used by `<PatientPage>` / `<CallPage>` to
   * pin the sidebar with `top: <Shell + ActionBar>` + a matching `height`
   * so it sits BELOW the sticky `<PatientChartActionBar>` row instead of
   * overlapping it.
   */
  style?: CSSProperties
  children?: React.ReactNode
  extraInfo?: string | React.ReactNode
  encounterId?: string
  sectionCounts?: Record<string, number | undefined>
}

export const PatientChart = ({
  extraInfo,
  onItemClick,
  currentPage,
  className,
  style,
  children,
  sectionCounts,
}: Props) => {
  return (
    <div
      style={style}
      className={[
        'w-[280px] overflow-auto border-r-1 border-r-[#EEEEEE] bg-[#FBFBFB] p-2 pb-8',
        className && className,
      ].join(' ')}
    >
      {!!extraInfo && <div className="text-light p-2 text-sm">{extraInfo}</div>}

      {!!children && <div className="mb-4">{children}</div>}

      <div className="flex flex-col">
        {items
          .filter((item) => !item.hidden)
          .map((item) => (
            <UnstyledButton
              className="flex justify-between !p-2"
              onClick={() => onItemClick(item)}
              key={item.title}
            >
              <div
                className={cn([
                  'flex items-center gap-1 text-sm text-[#7D7987]',
                  currentPage === item.dataKey && 'text-primary',
                  sectionCounts?.[item.dataKey] && 'font-extrabold',
                ])}
              >
                {currentPage === item.dataKey ? item.iconOpen : item.icon} {item.title}
              </div>
              {sectionCounts?.[item.dataKey] ? (
                <Badge variant="light" color="gray" size="sm">
                  {sectionCounts[item.dataKey]}
                </Badge>
              ) : null}
            </UnstyledButton>
          ))}
      </div>
    </div>
  )
}
